import { deriveActiveItinerary } from "../pocContext/pocContext";
import { sanitizeItineraryForLLM } from "../topDestinationContext/TopDestinationContext";
import { ROUTES } from "../../constant";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";
import { VOICE_MODE, VOICE_NOTES, VOICE_TOASTS } from "../../constant/voiceMode";
import { API_METHODS, ScreenTypes } from "../../enums";
import { iChatData } from "../../types";
import { fetchApi } from "../../utils/apiFunction";
import { getChatId } from "../../utils/chat/chatId";
import { isSelectionActiveInChat } from "../../utils/chat/chatSelection";
import { rollItineraryDatesToFuture } from "../../utils/chat/rollItineraryDatesToFuture";
import { toastMessage } from "../../utils/ToastProvider";
import { dropEmptyContentMessages } from "../../streaming/finalizeGuards";
import { sanitizeBodyForLLM } from "../../utils/messagesSanitizer";
import { voiceBus } from "../../services/voice/voiceBus";
import { Fragment, LiveTranscript } from "../../services/voice/liveTranscript";
import { attachMic, closeLivePeer, createOfferSdp, openLivePeer, openMic, setMicMuted } from "../../services/voice/livePeer";
import { voiceLevels } from "../../services/voice/voiceLevels";
import type { VoiceSession } from "./types";
import { VoiceRuntime, voiceLog, voiceMark, now, isRequestRunning, sendAppend } from "./runtime";
import { latestPrompt } from "./chatEntries";
import { endOpening, joinsOpening, openingScreen } from "./opening";
import { freshStats, micPermission, VoiceAnalytics } from "./voiceAnalytics";
import type { TranscriptIO } from "./transcriptIO";
import type { Bubbles } from "./bubbles";
import type { HeardAnswers } from "./heardAnswers";
import type { Exchanges } from "./exchanges";
import type { HandOffs } from "./handOffs";
import type { Narration } from "./narration";

const RESTART_REASONS = new Set(["expired", "connection_lost"]);

const toFragment = (event: any): Fragment => ({
  text: typeof event?.delta === "string" ? event.delta : "",
  startMs: Number(event?.start_ms) || 0,
  endMs: Number(event?.end_ms) || 0,
});

export const createLiveSession = (rt: VoiceRuntime, transcriptIO: TranscriptIO, voiceAnalytics: VoiceAnalytics, bubbles: Bubbles, heardAnswers: HeardAnswers, exchanges: Exchanges, handOffs: HandOffs, narration: Narration) => {
  const { live, sessionRef, turnRef, chatIdRef, lastFlushRef, mutedRef, backupRef, handlers, voiceEnabled, setVoiceStatus, setMuted } = rt;
  const { writeLine, writeUserLines, cleanerOf } = transcriptIO;
  const { trackEnd, trackMute } = voiceAnalytics;
  const { applyTargets, applyReplies, showRequestEarly, showLiveReply, dropLiveReply } = bubbles;
  const { fileAnswers, settleAnswer, showLiveAnswer } = heardAnswers;
  const { flushDeferred, resolveExchange, fileExchanges, settleExchange } = exchanges;
  const { recordBusy, closeLateLine, joinsHandOff, checkHandOff, joinsHeldLine } = handOffs;
  const { finishNarration, pumpSays, closeNarration, pumpProgress } = narration;

  const endVoice = (reason: string, remote = false) => {
    const session = sessionRef.current;
    if (!session) return;
    sessionRef.current = null;
    setVoiceStatus("off");
    voiceLevels.detach();
    mutedRef.current = false;
    setMuted(false);
    voiceBus.setLiveSessionId(null);
    voiceLog("end", { reason, liveSessionId: session.id });
    const shownId = session.earlyUserId;
    if (session.liveReply || (shownId && live.current.state.chatData.some((entry: iChatData) => entry.id === shownId))) {
      const exchange = session.transcript.settle();
      if (exchange) resolveExchange(session, exchange);
    }
    closeLateLine(session);
    fileExchanges(session, true);
    settleAnswer(session);
    const rest = session.transcript.drain();
    writeUserLines(session, rest.user, "gpt-live", null, null);
    if (!remote) finishNarration(turnRef.current);
    void backupRef.current?.flush();
    trackEnd(session, reason);
    const peer = session.peer;
    if (!peer) return;
    if (remote || session.startedAt === null || peer.channel.readyState !== "open") {
      closeLivePeer(peer);
      return;
    }
    const timer = setTimeout(() => {
      voiceLog("close.timeout", { liveSessionId: session.id });
      closeLivePeer(peer);
    }, VOICE_MODE.closeTimeoutMs);
    session.closed = () => {
      clearTimeout(timer);
      closeLivePeer(peer);
    };
    session.events += 1;
    peer.channel.send(JSON.stringify({ type: "session.close", event_id: `zv_${session.events}` }));
  };

  const offerRestart = () => toastMessage.info(VOICE_TOASTS.ended, VOICE_TOASTS.restart);

  const onLost = (session: VoiceSession) => {
    if (sessionRef.current !== session) return;
    endVoice("connection_lost", true);
    offerRestart();
  };

  const onLiveEvent = (session: VoiceSession, event: any) => {
    if (sessionRef.current !== session) {
      if (event?.type === "session.closed") session.closed?.();
      return;
    }
    const at = now();
    switch (event?.type) {
      case "session.started": {
        const first = session.startedAt === null;
        session.id = event?.session?.id ?? session.id;
        session.startedAt = at;
        session.lastActivityAt = at;
        voiceBus.setLiveSessionId(session.id);
        setVoiceStatus("on");
        voiceLog("session.started", { liveSessionId: session.id, ms: at - session.tapAt });
        if (first) triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_connected, "", "", "", { connect_ms: at - session.tapAt });
        if (first) sendOpening(session);
        session.started();
        return;
      }
      case "session.input_transcript.delta": {
        const fragment = toFragment(event);
        if (!fragment.text) return;
        endOpening(session, fragment.startMs);
        session.lastUserAt = at;
        session.lastActivityAt = at;
        if (joinsHandOff(session, fragment, at)) return;
        session.windowTurn = null;
        const running = turnRef.current;
        if (running?.open && running.voice) running.heard.push(fragment);
        if (joinsHeldLine(session, fragment, at)) return;
        if (session.answer) settleAnswer(session);
        const closed = session.transcript.addUser(fragment, !!session.handOff && !session.handOff.busy);
        if (closed) resolveExchange(session, closed);
        checkHandOff(session);
        return;
      }
      case "session.output_transcript.delta": {
        const fragment = toFragment(event);
        if (!fragment.text) return;
        if (session.lastAssistantAt === null) voiceMark(session, "output.first");
        session.lastAssistantAt = at;
        session.lastActivityAt = at;
        if (session.answer && !session.transcript.awaitsBusyReply()) {
          session.answer.fragments.push(fragment);
          writeLine(session, {
            speaker: "assistant",
            mode: "voice",
            handledBy: "gpt-live",
            text: fragment.text,
            startMs: fragment.startMs,
            endMs: fragment.endMs,
            delegationId: null,
            traceId: null,
          });
          showLiveAnswer(session);
          return;
        }
        if (joinsOpening(session, fragment)) {
          writeLine(session, {
            speaker: "assistant",
            mode: "voice",
            handledBy: "gpt-live",
            text: fragment.text,
            startMs: fragment.startMs,
            endMs: fragment.endMs,
            delegationId: null,
            traceId: null,
          });
          return;
        }
        const turn = turnRef.current;
        if (turn?.voice && (turn.open || !turn.narrated) && session.transcript.resumeNarration()) session.windowTurn = turn;
        const mark = session.transcript.addReply(fragment);
        if (!mark.narration) {
          showRequestEarly(session);
          showLiveReply(session);
        }
        const owner = mark.narration ? session.windowTurn : null;
        const xenia = mark.handledBy === "xenia" && owner;
        writeLine(session, {
          speaker: "assistant",
          mode: "voice",
          handledBy: mark.handledBy,
          text: fragment.text,
          startMs: fragment.startMs,
          endMs: fragment.endMs,
          delegationId: xenia ? owner.delegation?.id ?? null : null,
          traceId: xenia ? owner.traceId : null,
        });
        if (owner && !owner.narrated) owner.words.push(fragment);
        const timed = turnRef.current;
        if (timed?.timing?.ack && timed.timing.speechMs === undefined) {
          timed.timing.speechMs = at - timed.timing.at;
          voiceLog("assistant.first", { traceId: timed.traceId, ms: timed.timing.speechMs });
        }
        return;
      }
      case "session.delegation.created": {
        const id = event?.delegation?.id;
        if (typeof id !== "string") return;
        const offsetMs = Number(event?.offset_ms) || 0;
        session.lastActivityAt = at;
        const busy = isRequestRunning() || session.handOff !== null;
        voiceLog("delegation.received", { id, offsetMs, target: event?.delegation?.target, busy });
        dropLiveReply(session);
        const waiting = session.handOff && !session.handOff.busy ? session.handOff : null;
        if (waiting) {
          waiting.joined = [...(waiting.joined ?? []), { id, offsetMs }];
          voiceLog("delegation.joined", { id, into: waiting.id });
          return;
        }
        const late = !session.handOff && session.lateLine?.capped ? session.lateLine.turn.traceId : null;
        if (busy && !late) sendAppend(session, "session.commentary.append", id, VOICE_NOTES.busy);
        if (session.handOff) {
          recordBusy(session, { id, offsetMs, at, busy }, [], "");
          return;
        }
        session.handOff = { id, offsetMs, at, busy: busy && !late, late };
        checkHandOff(session);
        return;
      }
      case "session.commentary.appended": {
        const turn = session.acks.get(event?.client_event_id);
        if (!turn) return;
        session.acks.delete(event.client_event_id);
        if (turn.timing && !turn.timing.ack) {
          turn.timing.ack = true;
          voiceLog("commentary.ack.first", { traceId: turn.traceId, ms: at - turn.timing.at });
        }
        return;
      }
      case "session.closed": {
        const reason = typeof event?.reason === "string" ? event.reason : "";
        endVoice(reason || "closed", true);
        if (RESTART_REASONS.has(reason)) offerRestart();
        else toastMessage.info(VOICE_TOASTS.ended);
        return;
      }
      case "error":
        voiceLog("error", { error: event?.error });
        return;
      case "info":
        voiceLog("info", { code: event?.code, message: event?.message });
        return;
    }
  };

  const priorMessages = () => {
    const prior = dropEmptyContentMessages(latestPrompt(live.current.state.chatData));
    const messages = sanitizeBodyForLLM({ messages: prior }, { useToon: false }).messages;
    return messages.length > 0 ? { messages } : {};
  };

  const planOnScreen = () => {
    const { state: current, location: where } = live.current;
    const visible = current.isMobile
      ? where.pathname === ROUTES.ItineraryDetails
      : current.screen === ScreenTypes.ItineraryInfo || current.screen === ScreenTypes.SingleItinerary;
    const selected = current.selectedChatData;
    if (!visible || !isSelectionActiveInChat(selected, current.chatData, getChatId())) return undefined;
    if (!deriveActiveItinerary(selected).hasActiveItinerary) return undefined;
    return rollItineraryDatesToFuture(sanitizeItineraryForLLM(selected));
  };

  const sendOpening = (session: VoiceSession) => {
    const screen = openingScreen(planOnScreen(), live.current.state.chatData);
    if (!sendAppend(session, "session.commentary.append", null, VOICE_NOTES.opening(screen))) return;
    session.opening = { until: null, spokeTo: null };
    voiceMark(session, "opening.sent", { liveSessionId: session.id, screen });
  };

  const failStart = (session: VoiceSession, error: any, stage: string) => {
    if (sessionRef.current !== session) return;
    voiceLog("start.failed", { name: error?.name, message: error?.message });
    const micDenied = error?.name === "NotAllowedError";
    if (micDenied) triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_mic_denied);
    else triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_start_failed, "", "", "", { stage, error_name: error?.name ?? "" });
    endVoice("start-failed", true);
    if (micDenied) toastMessage.error(VOICE_TOASTS.micBlocked, VOICE_TOASTS.micHelp);
    else toastMessage.error(VOICE_TOASTS.unavailable, VOICE_TOASTS.tryAgain);
  };

  const takeMic = async (session: VoiceSession, mic: MediaStream) => {
    const peer = session.peer;
    if (sessionRef.current !== session || !peer) {
      mic.getTracks().forEach((track) => track.stop());
      return;
    }
    voiceMark(session, "mic.ready");
    await attachMic(peer, mic, mutedRef.current);
  };

  const connect = async (session: VoiceSession, startedSignal: Promise<void>, permission: Promise<string>) => {
    let stage = "mic";
    const micAttached = openMic()
      .then((mic) => takeMic(session, mic))
      .catch((error) => failStart(session, error, "mic"));
    try {
      const peer = openLivePeer({
        onEvent: (event) => handlers.current.onLiveEvent(session, event),
        onLost: () => handlers.current.onLost(session),
        onConnected: () => voiceMark(session, "peer.connected"),
        onOpen: () => voiceMark(session, "channel.open"),
      });
      session.peer = peer;
      voiceLevels.attach(peer);
      if ((await permission) !== "granted") await micAttached;
      if (sessionRef.current !== session) return;
      stage = "offer";
      const sdp = await createOfferSdp(peer.pc, VOICE_MODE.iceTimeoutMs);
      if (sessionRef.current !== session) return;
      voiceMark(session, "offer.ready");
      const currentItineray = planOnScreen();
      stage = "session";
      voiceMark(session, "session.sent");
      const result = await fetchApi(live.current.state.accessJWT, "xenia/voice/session", API_METHODS.POST, {
        sdp,
        ...priorMessages(),
        ...(chatIdRef.current ? { chatId: chatIdRef.current } : {}),
        ...(currentItineray ? { currentItineray } : {}),
      });
      if (sessionRef.current !== session) return;
      const answer = result?.data?.transport?.sdp;
      if (result?.statusCode !== 200 || typeof answer !== "string") {
        throw new Error(result?.message || "voice session refused");
      }
      voiceMark(session, "answer.received");
      session.id = result?.data?.session?.id ?? null;
      stage = "answer";
      await peer.pc.setRemoteDescription({ type: "answer", sdp: answer });
      voiceMark(session, "remote.set");
      stage = "started";
      await Promise.race([
        startedSignal,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("session.started timed out")), VOICE_MODE.startTimeoutMs),
        ),
      ]);
    } catch (error: any) {
      failStart(session, error, stage);
    }
  };

  const startVoice = () => {
    if (sessionRef.current || !voiceEnabled) return;
    voiceLevels.prepare();
    const at = now();
    let started: () => void = () => undefined;
    const startedSignal = new Promise<void>((resolve) => {
      started = resolve;
    });
    const session: VoiceSession = {
      id: null,
      peer: null,
      tapAt: at,
      startedAt: null,
      opening: null,
      transcript: new LiveTranscript(VOICE_MODE.quietMs),
      events: 0,
      lastUserAt: null,
      lastAssistantAt: null,
      lastActivityAt: at,
      handOff: null,
      windowTurn: null,
      earlyUserId: null,
      liveReply: null,
      heldLine: null,
      answer: null,
      answers: [],
      lateLine: null,
      lastExchange: null,
      written: new WeakSet(),
      busy: new WeakSet(),
      cleaner: null,
      targets: [],
      filings: [],
      acks: new Map(),
      started,
      closed: null,
      stats: freshStats(),
    };
    sessionRef.current = session;
    chatIdRef.current = live.current.state.chatData.length > 0 ? getChatId() : "";
    setVoiceStatus("connecting");
    voiceLog("start", { chatId: chatIdRef.current });
    const start = { chat: chatIdRef.current ? "existing" : "new", plan_open: !!planOnScreen() };
    const permission = micPermission();
    void permission.then((mic) =>
      triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_start_click, "", "", "", { ...start, mic }),
    );
    void connect(session, startedSignal, permission);
  };

  const toggleMute = () => {
    const session = sessionRef.current;
    if (!session) return;
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    if (session.peer) setMicMuted(session.peer, next);
    voiceLog("mute", { muted: next, liveSessionId: session.id });
    trackMute(session, next);
  };

  const tick = () => {
    const session = sessionRef.current;
    if (!session || session.startedAt === null) return;
    const at = now();
    checkHandOff(session);
    if (session.lateLine && at - session.lateLine.at >= VOICE_MODE.lateWordsMs) closeLateLine(session);
    cleanerOf(session).pump(session.transcript.pendingFragments(), at);
    pumpSays(session);
    closeNarration(session, at);
    settleExchange(session, at);
    pumpProgress(session, at);
    flushDeferred();
    fileAnswers(session);
    if (at - lastFlushRef.current >= VOICE_MODE.transcriptFlushS * 1000) {
      lastFlushRef.current = at;
      void backupRef.current?.flush();
    }
    const turn = turnRef.current;
    const narrating = !!turn && turn.voice && !turn.narrated;
    if (!isRequestRunning() && !narrating && at - session.lastActivityAt >= VOICE_MODE.idleCloseS * 1000) {
      endVoice("idle");
    }
  };

  const onCleaned = (session: VoiceSession) => {
    applyTargets(session);
    fileExchanges(session, false);
    applyReplies();
    fileAnswers(session);
    if (sessionRef.current !== session) return;
    showRequestEarly(session);
    showLiveReply(session);
  };

  return { endVoice, offerRestart, onLost, onLiveEvent, priorMessages, planOnScreen, connect, startVoice, toggleMute, tick, onCleaned };
};

export type LiveSession = ReturnType<typeof createLiveSession>;
